import { FindestRefTextBox } from "Components";
import { SearchableDropdown } from "Components/Shared/SearchableDropdown";
import { SearchUnitControllerSingleton } from "Controllers";
import { SpecificationUnitControllerSingleton } from "Controllers/SpecificationUnitController";
import { MathematicalOperationEnum, SearchPriorityEnum, SearchSubTermTypeEnum } from "Enums";
import { SavedQueryContext } from "Providers";
import { FC, useState, useContext, useRef, useCallback } from "react";
import { CaseContext } from "Stores";
import { TSavedQueryTempDTO, TSearchUnitDTO, TSpecificationUnitDTO } from "Types";
import { NewSpecificationUnit } from "./NewSpecificationUnit";

import styles from "./savedQueryFields.module.scss";
import { UnitComparisonDropdown } from "./UnitComparisonDropdown";

export const NewSpecificationUnits: FC = () => {

    // Context
    const { savedQuery, specificationUnits, updateSavedQuery, duplicateSavedQuery } = useContext(SavedQueryContext);
    const { caseState } = useContext(CaseContext);

    // Ref
    const minimumInputRef = useRef<HTMLInputElement>(null);
    const maximumInputRef = useRef<HTMLInputElement>(null);
    const unitValueInputRef = useRef<HTMLInputElement>(null);

    // State
    const [newUnitMathOperation, setNewUnitMathOperation] = useState(MathematicalOperationEnum.None);

    const operationToPlaceholderText = () => {
        switch (newUnitMathOperation) {
            case MathematicalOperationEnum.GreaterThan:
                return "Greater than";
            case MathematicalOperationEnum.LessThan:
                return "Less than";
            default:
                return "Greater than";
        }
    };

    const findDuplicatedSpecificationUnitInList = (specification: string, unit: string, specificationUnitList: TSpecificationUnitDTO[]): TSpecificationUnitDTO | undefined => {
        // safety-checks
        if (!specification || !unit || !specificationUnitList) return undefined;

        for (const specificationUnit of specificationUnitList) {
            if (specificationUnit.specification.trim().toLowerCase() === specification.trim().toLowerCase() &&
                specificationUnit.unit.trim().toLowerCase() === unit.trim().toLowerCase()) return specificationUnit;
        }

        return undefined;
    };

    const getSpecificationUnitByIdInList = (id: number, specificationUnitList: TSpecificationUnitDTO[]): TSpecificationUnitDTO | undefined => {
        // safety-checks
        if (id === null || id === undefined || !specificationUnitList) return undefined;

        for (const specificationUnit of specificationUnitList) {
            if (specificationUnit.id === id) return specificationUnit;
        }

        return undefined;
    };

    // Merge the update functions to remove duplicated code
    const changeSpecificationUnitSearchPriority = async (id: number, newSpecificationUnitSearchPriority: SearchPriorityEnum): Promise<void> => {
        // safety-checks
        if (!savedQuery) return;

        const foundSpecificationUnit: TSpecificationUnitDTO | undefined = getSpecificationUnitByIdInList(id, specificationUnits);
        // safety-checks
        if (!foundSpecificationUnit) return;

        if (savedQuery.isUserAdded && savedQuery.isOwnedByCurrentUser) {
            foundSpecificationUnit.searchType = newSpecificationUnitSearchPriority;
            const isSuccess = await SpecificationUnitControllerSingleton.updateAsync(foundSpecificationUnit, true);
            // update specification unit list in the saved query context
            if (isSuccess) {
                for (const specificationUnit of specificationUnits) {
                    if (specificationUnit.id === id) {
                        specificationUnit.searchType = newSpecificationUnitSearchPriority;
                    }
                }

                savedQuery.specificationUnits = [...specificationUnits];
                updateSavedQuery(savedQuery);
            }
        } else {
            duplicateSavedQuery(caseState.selectedOriginalTechnologyId, caseState.selectedCaseId, async (duplicatedSavedQuery: TSavedQueryTempDTO): Promise<void> => {
                // safety-checks
                if (!foundSpecificationUnit || !duplicatedSavedQuery) return;

                const duplicatedSpecificationUnit: TSpecificationUnitDTO | undefined =
                    findDuplicatedSpecificationUnitInList(foundSpecificationUnit.specification, foundSpecificationUnit.unit, duplicatedSavedQuery.specificationUnits);

                // safety-checks
                if (!duplicatedSpecificationUnit) return;

                duplicatedSpecificationUnit.searchType = newSpecificationUnitSearchPriority;
                const isSuccess = await SpecificationUnitControllerSingleton.updateAsync(duplicatedSpecificationUnit, true);

                // update specification unit list in the saved query context
                if (isSuccess) {
                    for (const specificationUnit of duplicatedSavedQuery.specificationUnits) {
                        if (specificationUnit.id === duplicatedSpecificationUnit.id) {
                            specificationUnit.searchType = newSpecificationUnitSearchPriority;
                        }
                    };

                    duplicatedSavedQuery.specificationUnits = [...duplicatedSavedQuery.specificationUnits];
                    updateSavedQuery(duplicatedSavedQuery);
                }
            });
        }
    };

    const changeUnitMathOperation = async (id: number, newMathOperation: MathematicalOperationEnum): Promise<void> => {
        // safety-checks
        if (!savedQuery) return;

        const foundSpecificationUnit: TSpecificationUnitDTO | undefined = getSpecificationUnitByIdInList(id, specificationUnits);
        // safety-checks
        if (!foundSpecificationUnit) return;

        if (savedQuery.isUserAdded && savedQuery.isOwnedByCurrentUser) {
            foundSpecificationUnit.mathLimitType = newMathOperation;
            const isSuccess = await SpecificationUnitControllerSingleton.updateAsync(foundSpecificationUnit, true);
            // update specification unit list in the saved query context
            if (isSuccess) {
                for (const specificationUnit of specificationUnits) {
                    if (specificationUnit.id === id) {
                        specificationUnit.mathLimitType = newMathOperation;
                    }
                };

                savedQuery.specificationUnits = [...specificationUnits];
                updateSavedQuery(savedQuery);
            }
        } else {
            duplicateSavedQuery(caseState.selectedOriginalTechnologyId, caseState.selectedCaseId, async (duplicatedSavedQuery: TSavedQueryTempDTO): Promise<void> => {
                // safety-checks
                if (!foundSpecificationUnit || !duplicatedSavedQuery) return;

                const duplicatedSpecificationUnit: TSpecificationUnitDTO | undefined =
                    findDuplicatedSpecificationUnitInList(foundSpecificationUnit.specification, foundSpecificationUnit.unit, duplicatedSavedQuery.specificationUnits);

                // safety-checks
                if (!duplicatedSpecificationUnit) return;

                duplicatedSpecificationUnit.mathLimitType = newMathOperation;
                const isSuccess = await SpecificationUnitControllerSingleton.updateAsync(duplicatedSpecificationUnit, true);

                // update specification unit list in the saved query context
                if (isSuccess) {
                    for (const specificationUnit of duplicatedSavedQuery.specificationUnits) {
                        if (specificationUnit.id === duplicatedSpecificationUnit.id) {
                            specificationUnit.mathLimitType = newMathOperation;
                        }
                    };

                    duplicatedSavedQuery.specificationUnits = [...duplicatedSavedQuery.specificationUnits];
                    updateSavedQuery(duplicatedSavedQuery);
                }
            });
        }
    };

    const deleteSpecificationUnit = async (id: number): Promise<void> => {
        // safety-checks
        if (!savedQuery) return;

        const newSpecificationUnits: TSpecificationUnitDTO[] = [];
        const foundSpecificationUnit: TSpecificationUnitDTO | undefined = getSpecificationUnitByIdInList(id, specificationUnits);
        // safety-checks
        if (!foundSpecificationUnit) return;

        if (savedQuery.isUserAdded && savedQuery.isOwnedByCurrentUser) {
            const isSuccess = await SpecificationUnitControllerSingleton.deleteAsync(id, true);
            // update specification unit list in the saved query context
            if (isSuccess) {
                for (const specificationUnit of specificationUnits) {
                    if (specificationUnit.id !== id) {
                        newSpecificationUnits.push(specificationUnit);
                    }
                }

                savedQuery.specificationUnits = [...newSpecificationUnits];
                updateSavedQuery(savedQuery);
            }
        } else {
            duplicateSavedQuery(caseState.selectedOriginalTechnologyId, caseState.selectedCaseId, async (duplicatedSavedQuery: TSavedQueryTempDTO): Promise<void> => {
                // safety-checks
                if (!foundSpecificationUnit || !duplicatedSavedQuery) return;

                const duplicatedSpecificationUnit: TSpecificationUnitDTO | undefined =
                    findDuplicatedSpecificationUnitInList(foundSpecificationUnit.specification, foundSpecificationUnit.unit, duplicatedSavedQuery.specificationUnits);

                // safety-checks
                if (!duplicatedSpecificationUnit) return;

                for (const specificationUnit of duplicatedSavedQuery.specificationUnits) {
                    if (specificationUnit.id !== duplicatedSpecificationUnit.id) {
                        newSpecificationUnits.push(specificationUnit);
                    }
                }

                const isSuccess = await SpecificationUnitControllerSingleton.deleteAsync(duplicatedSpecificationUnit.id, true);
                // update specification unit list in the saved query context
                if (isSuccess) {
                    duplicatedSavedQuery.specificationUnits = [...newSpecificationUnits];
                    updateSavedQuery(duplicatedSavedQuery);
                }
            });
        }
    };

    const changeSpecificationUnit = useCallback(async (id: number, value: number | string, searchSubTermType: SearchSubTermTypeEnum): Promise<boolean> => {
        // safety-checks
        if (!savedQuery) return false;

        const foundSpecificationUnit: TSpecificationUnitDTO | undefined = getSpecificationUnitByIdInList(id, specificationUnits);
        // safety-checks
        if (!foundSpecificationUnit) return false;

        if (savedQuery.isUserAdded && savedQuery.isOwnedByCurrentUser) {
            if (searchSubTermType === SearchSubTermTypeEnum.UnitValue) {
                foundSpecificationUnit.unitValue = value as number;
            } else if(searchSubTermType === SearchSubTermTypeEnum.MaximumUnitValue) {
                foundSpecificationUnit.maxUnitValue = value as number;
            } else {
                foundSpecificationUnit.unit = value as string;
            }
            const isSuccess = await SpecificationUnitControllerSingleton.updateAsync(foundSpecificationUnit, true);
            // update specification unit list in the saved query context
            if (isSuccess) {
                for (const specificationUnit of specificationUnits) {
                    if (specificationUnit.id === id) {
                        if (searchSubTermType === SearchSubTermTypeEnum.UnitValue) {
                            foundSpecificationUnit.unitValue = value as number;
                        } else if(searchSubTermType === SearchSubTermTypeEnum.MaximumUnitValue) {
                            foundSpecificationUnit.maxUnitValue = value as number;
                        } else {
                            foundSpecificationUnit.unit = value as string;
                        }
                    }
                }

                savedQuery.specificationUnits = [...specificationUnits];
                updateSavedQuery(savedQuery);
            }
            return isSuccess;
        } else {
            duplicateSavedQuery(caseState.selectedOriginalTechnologyId, caseState.selectedCaseId, async (duplicatedSavedQuery: TSavedQueryTempDTO): Promise<void> => {
                // safety-checks
                if (!foundSpecificationUnit || !duplicatedSavedQuery) return;

                const duplicatedSpecificationUnit: TSpecificationUnitDTO | undefined =
                    findDuplicatedSpecificationUnitInList(foundSpecificationUnit.specification, foundSpecificationUnit.unit, duplicatedSavedQuery.specificationUnits);

                // safety-checks
                if (!duplicatedSpecificationUnit) return;

                if (searchSubTermType === SearchSubTermTypeEnum.UnitValue) {
                    foundSpecificationUnit.unitValue = value as number;
                } else if(searchSubTermType === SearchSubTermTypeEnum.MaximumUnitValue) {
                    foundSpecificationUnit.maxUnitValue = value as number;
                } else {
                    foundSpecificationUnit.unit = value as string;
                }
                const isSuccess = await SpecificationUnitControllerSingleton.updateAsync(duplicatedSpecificationUnit, true);

                // update specification unit list in the saved query context
                if (isSuccess) {
                    for (const specificationUnit of duplicatedSavedQuery.specificationUnits) {
                        if (specificationUnit.id === duplicatedSpecificationUnit.id) {
                            if (searchSubTermType === SearchSubTermTypeEnum.UnitValue) {
                                foundSpecificationUnit.unitValue = value as number;
                            } else if(searchSubTermType === SearchSubTermTypeEnum.MaximumUnitValue) {
                                foundSpecificationUnit.maxUnitValue = value as number;
                            } else {
                                foundSpecificationUnit.unit = value as string;
                            }
                        }
                    }

                    duplicatedSavedQuery.specificationUnits = [...duplicatedSavedQuery.specificationUnits];
                    updateSavedQuery(duplicatedSavedQuery);
                }
            });
        }
        return true;
    }, [caseState.selectedCaseId, caseState.selectedOriginalTechnologyId, duplicateSavedQuery, savedQuery, specificationUnits, updateSavedQuery]);

    const createSpecificationUnit = async (newUnitText: string): Promise<boolean> => {
        newUnitText = newUnitText.trim();

        if (!savedQuery || newUnitText.length === 0) return false;

        // init new specification unit data
        const newSpecificationUnit: TSpecificationUnitDTO = {
            id: 0,
            specification: "",
            mathLimitType: newUnitMathOperation,
            unit: newUnitText,
            searchType: SearchPriorityEnum.Must,
            unitSynonyms: [],
            savedQueryId: savedQuery.guid
        };

        if (newUnitMathOperation === MathematicalOperationEnum.Between) {
            if (!minimumInputRef.current || !maximumInputRef.current) return false;

            newSpecificationUnit.unitValue = parseFloat(minimumInputRef.current.value);
            newSpecificationUnit.maxUnitValue = parseFloat(maximumInputRef.current.value);
            if (isNaN(newSpecificationUnit.unitValue) || isNaN(newSpecificationUnit.maxUnitValue)) return false;

        } else if (newUnitMathOperation === MathematicalOperationEnum.GreaterThan || newUnitMathOperation === MathematicalOperationEnum.LessThan) {
            if (!unitValueInputRef.current) return false;

            newSpecificationUnit.unitValue = parseFloat(unitValueInputRef.current.value);
            if (isNaN(newSpecificationUnit.unitValue)) return false;
        }

        if (savedQuery.isUserAdded && savedQuery.isOwnedByCurrentUser) {
            const createdSpecificationUnit = await SpecificationUnitControllerSingleton.createAsync(newSpecificationUnit, true);
            // update specification unit list in the saved query context
            savedQuery.specificationUnits = [...specificationUnits, createdSpecificationUnit];
            updateSavedQuery(savedQuery);
        } else {
            duplicateSavedQuery(caseState.selectedOriginalTechnologyId, caseState.selectedCaseId, async (duplicatedSavedQuery: TSavedQueryTempDTO): Promise<void> => {
                // safety-checks
                if (!duplicatedSavedQuery) return;

                newSpecificationUnit.savedQueryId = duplicatedSavedQuery.guid;
                const createdSpecificationUnit = await SpecificationUnitControllerSingleton.createAsync(newSpecificationUnit, true);
                
                // update specification unit list in the saved query context
                duplicatedSavedQuery.specificationUnits = [...specificationUnits, createdSpecificationUnit];
                updateSavedQuery(duplicatedSavedQuery);
            });
        }

        // empty add specification unit input refs
        if(minimumInputRef.current) {
            minimumInputRef.current.value = "";
        }
        if(maximumInputRef.current) {
            maximumInputRef.current.value = "";
        }
        if(unitValueInputRef.current) {
            unitValueInputRef.current.value = "";
        }


        return true;
    };

    const onUnitClick = async (result: TSearchUnitDTO): Promise<boolean> => {
        const isSuccess = await createSpecificationUnit(result.name);
        return isSuccess;
    };

    return (
        <div className={styles.userFlowDataList}>
            <h5 className={styles.dataListTitle}>Specifications</h5>
            <div className={styles.dataListInputContainer}>
                <UnitComparisonDropdown mathematicalOperation={newUnitMathOperation} selectMathOperation={setNewUnitMathOperation} />
                {newUnitMathOperation !== MathematicalOperationEnum.None ?
                    newUnitMathOperation === MathematicalOperationEnum.Between ?
                        <div style={{display: "flex"}}>
                            <FindestRefTextBox extraClassName={styles.unitValueInputContainer} extraClassNameInputElement={styles.unitValueInputField} placeholder="Minimum" ref={minimumInputRef} />
                            <span className={styles.rangeDivider}>-</span>
                            <FindestRefTextBox extraClassName={styles.unitValueInputContainer} extraClassNameInputElement={styles.unitValueInputField} placeholder="Maximum" ref={maximumInputRef} />
                        </div>
                        : <FindestRefTextBox extraClassName={styles.unitValueInputContainer} extraClassNameInputElement={styles.unitValueInputField} placeholder={operationToPlaceholderText()} ref={unitValueInputRef} />
                    : null}
                <SearchableDropdown<TSearchUnitDTO> placeholder="Unit" itemName="unit" asyncSearchFunction={SearchUnitControllerSingleton.searchAsync}
                    doClearValueOnClick={true} resultToIdAndName={SearchUnitControllerSingleton.searchUnitToIdAndName} onResultClickAsync={onUnitClick}
                    overriddenStyles={styles} doSearchWhenValueEmpty={true} />
            </div>
            {specificationUnits.map((specificationUnit: TSpecificationUnitDTO) => {
                return <NewSpecificationUnit key={specificationUnit.id} specificationUnit={specificationUnit} changeSpecificationUnitSearchPriority={changeSpecificationUnitSearchPriority}
                    deleteSpecificationUnit={deleteSpecificationUnit} editSearchSubTerm={changeSpecificationUnit} disabledPriority={false} changeUnitMathOperation={changeUnitMathOperation} />
            })}
        </div>
    );
};